import { useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Copy, MagnifyingGlass, CalendarBlank, Warning } from "@phosphor-icons/react";

/**
 * GcalDiagnoseCard — admin tool that calls
 *   GET /api/admin/gcal/diagnose/{owner_id}
 * and renders the JSON snapshot in a copyable, scrollable panel.
 *
 * Built because asking owners (or admins) to paste a curl/fetch into the
 * browser DevTools console kept failing — Chrome's "Self-XSS" warning
 * blocks paste until you literally type "allow pasting" first. This
 * card removes that friction entirely: click an owner, click run,
 * read the JSON.
 */
export default function GcalDiagnoseCard({ users = [] }) {
  const owners = useMemo(
    () =>
      (users || [])
        .filter((u) => u.role === "owner")
        .sort((a, b) => (a.email || "").localeCompare(b.email || "")),
    [users],
  );

  const [ownerId, setOwnerId] = useState("");
  const [manualId, setManualId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  async function run() {
    const target = (ownerId || manualId || "").trim();
    if (!target) {
      toast.error("Pick an owner or paste an owner_id first.");
      return;
    }
    setLoading(true);
    setErr(null);
    setData(null);
    try {
      const r = await api.get(`/admin/gcal/diagnose/${encodeURIComponent(target)}`);
      setData(r.data);
    } catch (e) {
      const msg = formatApiError(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  async function copyJson() {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      toast.success("Copied JSON to clipboard.");
    } catch {
      toast.error("Copy failed — select the text and copy manually.");
    }
  }

  const tiles = data
    ? [
        { label: "Trucks (DB)", value: data.trucks_count ?? 0 },
        { label: "Schedules", value: data.schedules_count ?? 0 },
        { label: "Pending (needs-fix)", value: data.pending_count ?? 0 },
        {
          label: "Live in Google now",
          value: data?.live_google_fetch?.event_count ?? 0,
          highlight: true,
        },
      ]
    : [];

  return (
    <div className="space-y-3" data-testid="gcal-diagnose-card">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
        <div className="flex-1 min-w-0">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
            Owner
          </label>
          <select
            value={ownerId}
            onChange={(e) => {
              setOwnerId(e.target.value);
              setManualId("");
            }}
            className="w-full mt-1 px-3 py-2 rounded-md border border-input bg-background text-sm"
            data-testid="gcal-diagnose-owner-select"
          >
            <option value="">— pick an owner —</option>
            {owners.map((u) => (
              <option key={u.id} value={u.id}>
                {u.email || u.name || u.id}
                {u.is_pro ? " · Pro" : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:w-64">
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
            …or paste owner_id
          </label>
          <Input
            value={manualId}
            onChange={(e) => {
              setManualId(e.target.value);
              setOwnerId("");
            }}
            placeholder="uuid…"
            className="mt-1"
            data-testid="gcal-diagnose-manual-input"
          />
        </div>
        <Button
          onClick={run}
          disabled={loading || (!ownerId && !manualId.trim())}
          className="sm:w-auto"
          data-testid="gcal-diagnose-run-btn"
        >
          <MagnifyingGlass weight="bold" className="h-4 w-4 mr-1.5" />
          {loading ? "Running…" : "Run diagnose"}
        </Button>
      </div>

      {err && (
        <div
          className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
          data-testid="gcal-diagnose-error"
        >
          <Warning weight="duotone" className="h-4 w-4 text-destructive flex-none mt-0.5" />
          <div className="text-destructive break-words">{err}</div>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {tiles.map((t) => (
              <div
                key={t.label}
                className={
                  "rounded-md border p-3 " +
                  (t.highlight
                    ? "border-primary/40 bg-primary/5"
                    : "border-border bg-muted/20")
                }
                data-testid={`gcal-diagnose-tile-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                  {t.label}
                </div>
                <div className="text-2xl font-display font-semibold mt-0.5">{t.value}</div>
              </div>
            ))}
          </div>

          {data?.live_google_fetch?.error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
              <div className="font-semibold text-destructive mb-1 flex items-center gap-1.5">
                <Warning weight="duotone" className="h-4 w-4" />
                Live Google fetch failed
              </div>
              <div className="text-xs font-mono break-words">
                {data.live_google_fetch.error}
              </div>
            </div>
          )}

          {data?.live_google_fetch?.events?.length > 0 && (
            <div className="rounded-md border border-border bg-card">
              <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
                <CalendarBlank weight="duotone" className="h-4 w-4 text-primary" />
                <div className="font-semibold text-sm">
                  What Google returns right now (next 365d)
                </div>
              </div>
              <ul className="divide-y divide-border max-h-72 overflow-y-auto text-xs">
                {data.live_google_fetch.events.map((e) => {
                  const trucksWithThisId = (data.trucks || []).filter(
                    (t) => t.gcal_event_id === e.id,
                  );
                  const inDb = trucksWithThisId.length > 0;
                  return (
                    <li
                      key={e.id}
                      className="px-3 py-2 flex items-start gap-2"
                      data-testid={`gcal-diagnose-event-${e.id}`}
                    >
                      <span
                        className={
                          "inline-block w-2 h-2 rounded-full mt-1.5 flex-none " +
                          (inDb ? "bg-emerald-500" : "bg-amber-500")
                        }
                        title={inDb ? "Present in TruckTrackr DB" : "Missing from TruckTrackr DB"}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">
                          {e.summary || "(no title)"}
                        </div>
                        <div className="text-muted-foreground truncate">
                          {e.start} · {e.location || "no location"} · status={e.status}
                        </div>
                        <div className="text-[10px] font-mono text-muted-foreground truncate">
                          id={e.id}
                          {inDb ? ` · matched ${trucksWithThisId.length} truck row(s)` : " · NO matching truck row"}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] text-muted-foreground">
              Snapshot taken {data.checked_at}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={copyJson}
              data-testid="gcal-diagnose-copy-btn"
            >
              <Copy weight="bold" className="h-3.5 w-3.5 mr-1.5" />
              Copy full JSON
            </Button>
          </div>

          <details className="rounded-md border border-border bg-muted/10">
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold select-none">
              Raw JSON
            </summary>
            <pre className="text-[10px] font-mono p-3 overflow-x-auto max-h-[420px] whitespace-pre-wrap break-words">
              {JSON.stringify(data, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}
